import React from 'react';

import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';

import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';

import { PageProps } from '../../../components/types/coretypes';
import { BasicCodeCombo } from '../core/BasicCodeCombo';
import { AutoCodeCombo } from '../combos';
import { FDataGrid } from '../grid';
import { FWorkbenchShell } from '../workbench';

import BieOnboardingStatusChip from './BieOnboardingStatusChip';
import { fetchBieLoadSessionScopeCombo, findBieLoadSessions } from './bieloadsession.apis';
import {
  BieLoadSession,
  BieLoadSessionComboItem,
  BieLoadSessionFindRequest,
  BieLoadSessionScopeComboItem,
} from './bieloadsession.models';
import { ComboRow } from '../core/coremodels';

import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';

import {
  //findBieLoadSessions,
  reopenBieLoadSession,
} from './bieloadsession.apis';

const PAGE_LOAD_SESSION_WIZARD = 'bieonboardingwizard';
const PAGE_ONBOARDING_WORKBENCH = 'bieonboardingreview';

type LoadSessionFilters = Pick<
  BieLoadSessionFindRequest,
  'search' | 'stateCode' | 'scopeCode' | 'sourceSystem'
>;

export interface IBieOnboardingLoadSessionManagerProps extends PageProps {
  initialFilters?: Partial<LoadSessionFilters>;
}

interface IBieOnboardingLoadSessionManagerState {
  loading: boolean;
  errorMessage: string;
  sessions: BieLoadSession[];
  scopes: BieLoadSessionScopeComboItem[];
  filters: Required<LoadSessionFilters>;
  pageOffset: number;
  pageLimit: number;
  totalCount: number;
  selectedLoadSessionId: number | null;
  reopeningLoadSessionId: number | null;
}

const emptyFilters: Required<LoadSessionFilters> = {
  search: '',
  stateCode: '',
  scopeCode: '',
  sourceSystem: '',
};

const loadSessionStates = [
  { code: 'DRAFT', name: 'Draft' },
  { code: 'STAGED', name: 'Staged' },
  { code: 'REJECTED', name: 'Rejected' },
  { code: 'VALIDATED', name: 'Validated' },
  { code: 'PUBLISHED', name: 'Published' },
  { code: 'CANCELLED', name: 'Cancelled' },
  { code: 'ROLLEDBACK', name: 'Rolled Back' },
];

export default class BieOnboardingLoadSessionManager extends React.Component<
  IBieOnboardingLoadSessionManagerProps,
  IBieOnboardingLoadSessionManagerState
> {
  private mounted = false;
  private requestVersion = 0;

  public constructor(props: IBieOnboardingLoadSessionManagerProps) {
    super(props);

    this.state = {
      loading: false,
      errorMessage: '',
      sessions: [],
      scopes: [],
      filters: {
        ...emptyFilters,
        ...props.initialFilters,
      },
      pageOffset: 0,
      pageLimit: 50,
      totalCount: 0,
      selectedLoadSessionId: null,
      reopeningLoadSessionId: null,
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.init();
    //void this.loadSessions(true);
  }

  public componentWillUnmount(): void {
    this.mounted = false;
    this.requestVersion += 1;
  }

  public render(): React.ReactNode {
    const paginationModel: GridPaginationModel = {
      page: Math.floor(this.state.pageOffset / this.state.pageLimit),
      pageSize: this.state.pageLimit,
    };

    return (
      <FWorkbenchShell
        //title={this.props.title || 'Institutional Onboarding Workbench'}
        title={'Institutional Onboarding Workbench'}
        subtitle="Create, stage, validate and publish institutional and historical baseline data loads."
        icon={<DatasetOutlinedIcon />}
        loading={this.state.loading}
        error={this.state.errorMessage}
        onRefresh={this.handleRefresh}
        actions={
          <Button
            variant="contained"
            disabled={this.state.loading}
            startIcon={<AddOutlinedIcon />}
            onClick={this.handleAddSession}
          >
            New Load Session
          </Button>
        }
        filters={this.renderFilters()}
      >
        <FDataGrid
          title="Load Sessions"
          subtitle="Select a Load Session to edit its context, upload a workbook, or open the onboarding Workbench."
          rows={this.state.sessions}
          columns={this.columns}
          loading={this.state.loading}
          height={570}

          masterDetail
          showSelectionControl
          selectedRowId={this.state.selectedLoadSessionId}
          onSelectedRowChange={(row) =>
            this.setState({
              selectedLoadSessionId: row
                ? Number((row as BieLoadSession).rid)
                : null,
            })
          }

          showToolbar
          showColumnSelector
          showFilterButton
          showDensitySelector
          enableCsvExport
          enablePrintExport={false}
          csvFileName="BIE_Onboarding_Load_Sessions"
          csvUtf8WithBom

          refresh={this.handleRefresh}
          addRow={this.handleAddSession}

          emptyState={{
            title: 'No Load Sessions found',
            message:
              this.getActiveFilterCount() > 0
                ? 'No active Load Sessions match the current filters.'
                : 'Create the first Load Session to begin institutional onboarding.',
            icon: <DatasetOutlinedIcon />,
            actionLabel: 'New Load Session',
            onAction: this.handleAddSession,
          }}

          paginationMode="server"
          rowCount={this.state.totalCount}
          paginationModel={paginationModel}
          onPaginationModelChange={this.handlePaginationModelChange}
          pageSizeOptions={[25, 50, 100, 250]}

          getRowId={(row: any) => row.rid}
          disableRowSelectionOnClick={false}
          density="compact"
        />
      </FWorkbenchShell>
    );
  }

   private init = async () => {
     this.setState({ loading: true, errorMessage:'' });
  
      try {
        const [scopes] = await Promise.all([fetchBieLoadSessionScopeCombo()]);
        this.setState({ scopes });
        await this.loadSessions(true);
      } catch(error:any) {
        this.setState({ errorMessage:error?.message || String(error) });
      } finally {
        this.setState({ loading:false });
      }
    };

  private readonly columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Load Session',
      minWidth: 230,
      flex: 1,
      renderCell: (params) => {
        const session = params.row as BieLoadSession;

        return (
          <Stack spacing={0.15} sx={{ py: 0.75 }}>
            <Typography variant="body2" fontWeight={700}>
              {session.name || session.shortcode}
            </Typography>
            <Typography variant="caption" color="text.secondary" fontFamily="monospace">
              {session.shortcode}
            </Typography>
            {session.scopeName && (
              <Typography variant="caption" color="text.disabled">
                {session.scopeName}
              </Typography>
            )}
          </Stack>
        );
      },
    },
    {
      field: 'sourceOrganization',
      headerName: 'Institution',
      minWidth: 210,
      flex: 1,
      renderCell: (params) => {
        const session = params.row as BieLoadSession;
        const institutionName = this.getInstitutionName(session);
        const institutionCode = this.getInstitutionCode(session);

        return (
          <Stack spacing={0.15} sx={{ py: 0.75 }}>
            <Typography variant="body2" fontWeight={600}>
              {institutionName || session.sourceOrganization || '—'}
            </Typography>
            {(institutionCode || session.sourceSystem) && (
              <Typography variant="caption" color="text.secondary">
                {[institutionCode, session.sourceSystem].filter(Boolean).join(' · ')}
              </Typography>
            )}
          </Stack>
        );
      },
    },
    {
      field: 'coverageStartDate',
      headerName: 'Historical Coverage',
      minWidth: 240,
      renderCell: (params) => {
        const session = params.row as BieLoadSession;
        const observedStart = session.observedCoverageStartDate;
        const observedEnd = session.observedCoverageEndDate;

        return (
          <Stack spacing={0.15} sx={{ py: 0.75 }}>
            <Typography variant="body2" fontWeight={600}>
              {this.formatDate(observedStart || session.coverageStartDate)}
              {' – '}
              {this.formatDate(observedEnd || session.coverageEndDate)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Cut-off: {this.formatDate(session.historicalCutoffDate)}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'stateCode',
      headerName: 'State',
      width: 145,
      renderCell: (params) => {
        const session = params.row as BieLoadSession;
        return (
          <BieOnboardingStatusChip
            kind="LOAD_SESSION"
            value={session.stateCode}
            label={session.stateName || undefined}
          />
        );
      },
    },
    {
      field: 'workbook',
      headerName: 'Workbook',
      minWidth: 220,
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const session = params.row as BieLoadSession;

        if (!session.workbook.filename) {
          return <Chip size="small" variant="outlined" label="Not uploaded" />;
        }

        return (
          <Stack spacing={0.15} sx={{ py: 0.75, minWidth: 0 }}>
            <Tooltip title={session.workbook.filename} arrow>
              <Typography variant="body2" fontWeight={600} noWrap>
                {session.workbook.filename}
              </Typography>
            </Tooltip>
            {session.workbook.version && (
              <Typography variant="caption" color="text.secondary">
                Version {session.workbook.version}
              </Typography>
            )}
          </Stack>
        );
      },
    },
    {
      field: 'records',
      headerName: 'Records',
      width: 105,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (_value, row: any) => row.statistics?.onboardingItemCount ?? 0,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={700}>
          {this.formatNumber(params.value)}
        </Typography>
      ),
    },
    {
      field: 'validation',
      headerName: 'Validation',
      width: 170,
      sortable: false,
      renderCell: (params) => {
        const session = params.row as BieLoadSession;
        return (
          <Stack spacing={0.5} sx={{ py: 0.5 }}>
            <BieOnboardingStatusChip
              kind="VALIDATION"
              value={session.statistics.validationStatus || 'EMPTY'}
            />
            <BieOnboardingStatusChip
              kind="READINESS"
              value={session.statistics.validationReady}
            />
          </Stack>
        );
      },
    },
    {
      field: 'issues',
      headerName: 'Issues',
      width: 125,
      sortable: false,
      renderCell: (params) => {
        const session = params.row as BieLoadSession;
        const issues = Number(session.statistics.issueCount || 0);
        const blocking = Number(session.statistics.blockingIssueCount || 0);

        return (
          <Stack direction="row" spacing={0.5}>
            <Chip
              size="small"
              variant="outlined"
              color={issues > 0 ? 'warning' : 'default'}
              label={this.formatNumber(issues)}
            />
            {blocking > 0 && (
              <Chip size="small" color="error" label={this.formatNumber(blocking)} />
            )}
          </Stack>
        );
      },
    },
    {
      field: 'created',
      headerName: 'Created',
      width: 165,
      valueFormatter: (value) => this.formatDateTime(String(value || '')),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => this.renderRowActions(params.row as BieLoadSession),
    },
  ];

  private renderFilters(): React.ReactNode {
    const disabled = this.state.loading;

    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <FilterAltOutlinedIcon color="action" fontSize="small" />
            <Typography variant="subtitle2" fontWeight={700}>
              Load Session Filters
            </Typography>
            {this.getActiveFilterCount() > 0 && (
              <Chip
                size="small"
                color="primary"
                label={`${this.getActiveFilterCount()} active`}
              />
            )}
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'minmax(300px, 1fr) repeat(3, minmax(180px, 220px))',
              },
              gap: 1.5,
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Search Load Sessions"
              placeholder="Session code, name, organization or workbook"
              value={this.state.filters.search}
              disabled={disabled}
              onChange={this.handleSearchChange}
              onKeyDown={this.handleSearchKeyDown}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              select
              fullWidth
              size="small"
              label="State"
              value={this.state.filters.stateCode}
              disabled={disabled}
              onChange={this.handleStateCodeChange}
            >
              <MenuItem value="">All States</MenuItem>
              {loadSessionStates.map((state) => (
                <MenuItem key={state.code} value={state.code}>
                  {state.name}
                </MenuItem>
              ))}
            </TextField>

            {/* <BasicCodeCombo
              fullWidth
              size="small"
              label="Load Scope"
              placeholder="All Load Scope"
              items={this.state.scopes}
              value={this.state.filters.scopeCode || null}
              disabled={disabled}
              onChange={this.handleScopeCodeChange0}
            /> */}
            <AutoCodeCombo
              service="bieloadscope"
              label="Load Scope"
              value={this.state.filters.scopeCode || null}
              disabled={disabled}
              onChange={this.handleScopeCodeChange}
              minimumSearchLength={0}
              pageLimit={25}
              placeholder="All Load Scopes"
            />

            <TextField
              fullWidth
              size="small"
              label="Source System"
              placeholder="e.g. JDE, SAGE, EXCEL"
              value={this.state.filters.sourceSystem}
              disabled={disabled}
              onChange={this.handleSourceSystemChange}
            />
          </Box>

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="contained"
              disabled={disabled}
              startIcon={<FilterAltOutlinedIcon />}
              onClick={this.handleApplyFilters}
            >
              Apply
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              disabled={disabled || this.getActiveFilterCount() === 0}
              startIcon={<ClearOutlinedIcon />}
              onClick={this.handleClearFilters}
            >
              Clear
            </Button>
          </Stack>
        </Stack>
      </Paper>
    );
  }

  private renderRowActions(session: BieLoadSession): React.ReactNode {
    const stateCode = String(session.stateCode || '').toUpperCase();
    const canReopen = stateCode === 'REJECTED' || stateCode === 'STAGED';
    const reopening = this.state.reopeningLoadSessionId === Number(session.rid);

    return (
      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
        {session.actions.canEditContext && (
          <Tooltip title="Edit Load Context" arrow>
            <IconButton
              size="small"
              disabled={this.state.loading}
              onClick={(event) => this.handleEditSession(event, session)}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {session.actions.canUploadWorkbook && (
          <Tooltip
            title={session.workbook.available ? 'Replace Workbook' : 'Upload Workbook'}
            arrow
          >
            <IconButton
              size="small"
              color="primary"
              disabled={this.state.loading}
              onClick={(event) => this.handleUploadWorkbook(event, session)}
            >
              <UploadFileOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {canReopen && (
          <Tooltip title="Reopen for Correction" arrow>
            <span>
              <IconButton
                size="small"
                color="warning"
                disabled={this.state.loading || reopening}
                onClick={(event) =>
                  void this.handleReopenForCorrection(event, session)
                }
              >
                <ReplayOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}

        <Tooltip title={this.getWorkbenchActionTitle(session)} arrow>
          <span>
            <IconButton
              size="small"
              color="primary"
              disabled={this.state.loading || !session.actions.canOpenWorkbench}
              onClick={(event) => this.handleOpenWorkbench(event, session)}
            >
              <OpenInNewOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    );
  }

  private async loadSessions(initialLoad = false): Promise<void> {
    const requestVersion = ++this.requestVersion;
    this.setState({ loading: true, errorMessage: '' });

    try {
      const result = await findBieLoadSessions({
        search: this.state.filters.search,
        stateCode: this.state.filters.stateCode,
        scopeCode: this.state.filters.scopeCode,
        sourceSystem: this.state.filters.sourceSystem,
        status: 1,
        pageOffset: this.state.pageOffset,
        pageLimit: this.state.pageLimit,
      });

      if (!this.mounted || requestVersion !== this.requestVersion) {
        return;
      }

      const selectedExists = result.rows.some(
        (session) => session.rid === this.state.selectedLoadSessionId,
      );

      this.setState({
        sessions: result.rows,
        totalCount: result.totalCount,
        loading: false,
        errorMessage: '',
        selectedLoadSessionId: selectedExists
          ? this.state.selectedLoadSessionId
          : null,
      });
    } catch (error) {
      if (!this.mounted || requestVersion !== this.requestVersion) {
        return;
      }

      this.setState({
        loading: false,
        errorMessage: this.getErrorMessage(error),
      });
    }
  }

  private readonly handleSearchChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    this.setState((state) => ({
      filters: { ...state.filters, search: event.target.value },
    }));
  };

  private readonly handleStateCodeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    this.setState((state) => ({
      filters: { ...state.filters, stateCode: event.target.value },
    }));
  };

  private readonly handleScopeCodeChange = (
    value: number | string | null,
  ): void => {
    this.setState((state) => ({
      filters: {
        ...state.filters,
        scopeCode: value === null
          ? ''
          : String(value).trim().toUpperCase(),
      },
    }));
  };

  private readonly handleScopeCodeChange0 = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    this.setState((state) => ({
      filters: {
        ...state.filters,
        scopeCode: event.target.value.toUpperCase(),
      },
    }));
  };

  private readonly handleSourceSystemChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    this.setState((state) => ({
      filters: { ...state.filters, sourceSystem: event.target.value },
    }));
  };

  private readonly handleSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.handleApplyFilters();
    }
  };

  private readonly handleApplyFilters = (): void => {
    this.setState(
      { pageOffset: 0, selectedLoadSessionId: null },
      () => void this.loadSessions(false),
    );
  };

  private readonly handleClearFilters = (): void => {
    this.setState(
      {
        filters: { ...emptyFilters },
        pageOffset: 0,
        selectedLoadSessionId: null,
      },
      () => void this.loadSessions(false),
    );
  };

  private readonly handleRefresh = (): void => {
    void this.loadSessions(false);
  };

  private readonly handlePaginationModelChange = (
    model: GridPaginationModel,
  ): void => {
    const pageLimit = model.pageSize > 0 ? model.pageSize : 50;

    this.setState(
      {
        pageOffset: model.page * pageLimit,
        pageLimit,
        selectedLoadSessionId: null,
      },
      () => void this.loadSessions(false),
    );
  };

  private readonly openWizard = (
    session: BieLoadSession | null,
    startStep: 'LOAD_CONTEXT' | 'WORKBOOK_UPLOAD',
  ): void => {
    const {scopes} = this.state;
    const setWizardParam = this.props.onParam('set'); 

    setWizardParam('mode', session ? 'EDIT' : 'ADD');
    setWizardParam('loadsessionid', session?.rid ?? null);
    setWizardParam('stp', session?.stamp ?? null);
    setWizardParam('loadScopeOptions', scopes);
    setWizardParam('startstep', startStep);

    this.props.setPage(PAGE_LOAD_SESSION_WIZARD);
  };

  private readonly handleAddSession = (): void => {
    this.openWizard(null, 'LOAD_CONTEXT');
  };

  private readonly handleEditSession = (
    event: React.MouseEvent<HTMLButtonElement>,
    session: BieLoadSession,
  ): void => {
    event.preventDefault();
    event.stopPropagation();
    this.setState({ selectedLoadSessionId: session.rid });
    this.openWizard(session, 'LOAD_CONTEXT');
  };

  private readonly handleUploadWorkbook = (
    event: React.MouseEvent<HTMLButtonElement>,
    session: BieLoadSession,
  ): void => {
    event.preventDefault();
    event.stopPropagation();
    this.setState({ selectedLoadSessionId: session.rid });
    this.openWizard(session, 'WORKBOOK_UPLOAD');
  };

  private readonly handleOpenWorkbench = (
    event: React.MouseEvent<HTMLButtonElement>,
    session: BieLoadSession,
  ): void => {
    event.preventDefault();
    event.stopPropagation();
    this.setState({ selectedLoadSessionId: session.rid });

    const setWorkbenchParam = this.props.onParam('set');
    setWorkbenchParam('loadsessionid', session.rid);
    setWorkbenchParam('stp', session.stamp);
    setWorkbenchParam('workbenchorigin', this.props.page || 'bieonboardingsession');
    this.props.setPage(PAGE_ONBOARDING_WORKBENCH);
  };

  private readonly handleReopenForCorrection = async (
    event: React.MouseEvent<HTMLButtonElement>,
    session: BieLoadSession,
  ): Promise<void> => {
    event.preventDefault();
    event.stopPropagation();

    if (!session.rid || !session.stamp) {
      if (this.props.showNotification) {
        this.props.showNotification(
          'Refresh the Load Session before reopening it.',
          'warning',
        );
      }
      return;
    }

    this.setState({
      reopeningLoadSessionId: Number(session.rid),
      errorMessage: '',
    });

    try {
      const result = await reopenBieLoadSession({
        loadSessionId: Number(session.rid),
        stamp: session.stamp,
      });

      this.props.onParam('set')(
        'loadsessionid',
        Number(session.rid),
      );

      this.props.onParam('set')(
        'stp',
        result.stamp,
      );

      this.props.onParam('set')(
        'mode',
        'EDIT',
      );

      if (this.props.showNotification) {
        this.props.showNotification(
          'Load Session reopened. Reset Staging before uploading the corrected workbook.',
          'success',
        );
      }

      await this.loadSessions(false);

      this.props.setPage(PAGE_LOAD_SESSION_WIZARD);
    } catch (error) {
      this.setState({
        errorMessage: this.getErrorMessage(error),
      });

      if (this.props.showNotification){
        this.props.showNotification(
          this.getErrorMessage(error),
          'error',
        );
      }
    } finally {
      if (this.mounted) {
        this.setState({
          reopeningLoadSessionId: null,
        });
      }
    }
  };

  private getWorkbenchActionTitle(session: BieLoadSession): string {
    if (!session.actions.canOpenWorkbench) {
      return 'Upload and stage a workbook before opening the Workbench';
    }

    switch (String(session.stateCode || '').toUpperCase()) {
      case 'DRAFT':
        return 'Open Draft Workbench';
      case 'STAGED':
        return 'Open Staged Workbench';
      case 'REJECTED':
        return 'Open Workbench to Correct Issues';
      case 'VALIDATED':
        return 'Open Validated Workbench';
      case 'PUBLISHED':
        return 'Open Published Workbench';
      default:
        return 'Open Onboarding Workbench';
    }
  }

  private getInstitutionName(session: BieLoadSession): string {
    return String(
      session.extraInfo?.institutionName ||
        session.extraInfo?.institution?.name ||
        '',
    );
  }

  private getInstitutionCode(session: BieLoadSession): string {
    return String(
      session.extraInfo?.institutionCode ||
        session.extraInfo?.institution?.code ||
        '',
    );
  }

  private getActiveFilterCount(): number {
    return Object.values(this.state.filters).filter(
      (value) => String(value ?? '').trim() !== '',
    ).length;
  }

  private getErrorMessage(error: unknown): string {
    return error instanceof Error
      ? error.message
      : 'An unexpected error occurred while retrieving the onboarding Load Sessions.';
  }

  private formatNumber(value: unknown): string {
    const numericValue = Number(value);
    return Number.isFinite(numericValue)
      ? new Intl.NumberFormat().format(numericValue)
      : '0';
  }

  private formatDate(value: string | null | undefined): string {
    if (!value) return '—';

    const normalized = String(value).trim();
    const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return normalized;

    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    if (Number.isNaN(date.getTime())) return normalized;

    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    }).format(date);
  }

  private formatDateTime(value: string | null | undefined): string {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);

    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }
}
